import { FormProperties, PageField } from "./schema"

export interface PropertyRenderer<T extends FormProperties<any, PageField>> {
  type: string;
  (config: T): React.ReactElement
}

declare const knownFields: unique symbol

/**
 * A registry that renders at least the page-local field types in TExtra, so
 * a registry knowing more types is assignable to one required to know fewer.
 */
export type Registry<TExtra extends PageField = never> = Readonly<
  Record<string, PropertyRenderer<any>>
> & { readonly [knownFields]?: (field: TExtra) => void }

export function defineRegistry(
  ...renderers: PropertyRenderer<any>[]
): Registry {
  return extendRegistry({}, ...renderers)
}

export function extendRegistry<
  TBase extends PageField,
  TAdded extends PageField[],
>(
  base: Registry<TBase>,
  ...renderers: { [K in keyof TAdded]: PropertyRenderer<TAdded[K]> }
): Registry<TBase | TAdded[number]> {
  const registry: Record<string, PropertyRenderer<any>> = { ...base }

  renderers.forEach((renderer) => {
    if (renderer.type in registry)
      throw new Error(
        `Form property type ${renderer.type} is already registered.`,
      )
    registry[renderer.type] = renderer
  })

  return registry
}

export function getRenderer(
  registry: Registry,
  formProperties: FormProperties<any, PageField>,
) {
  const renderer = registry[formProperties.type]
  if (!renderer)
    throw new Error(
      `Form property type ${formProperties.type} is not registered.`,
    )
  return renderer
}
